import React, { ReactElement, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import DocumentItem from 'components/pages/documents/DocumentItem';
import DocumentSetSwitch from 'components/pages/documents/DocumentSetSwitch';
import {
    CS_DOCUMENTS,
    DOC_SECTIONS,
    DOC_SET_LABELS,
    DOC_SET_QUERY_KEY,
    DocSection,
    DocSetEnum,
    DocumentDef,
    EN_DOCUMENTS,
    EN_SECTION_LABELS,
    IS_PARTIAL,
    csHref,
    enHref,
    hasFreshEn,
} from 'components/pages/documents/documents.data';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import { TNamespaceEnum } from 'types/types';
import styles from './DocumentsBlock.module.scss';

interface Props {
    initialSet?: DocSetEnum;
}

/*
    Seznam dokumentů ke stažení s přepínačem SADY (česká / anglická). Přepínač
    nemění jazyk aplikace — rám stránky zůstává vždy český, viz documents.data.ts.
 */
const DocumentsBlock = ({
    initialSet = DocSetEnum.cs,
}: Props): ReactElement => {
    const { t } = useTranslation();
    const router = useRouter();
    const [set, setSet] = useState<DocSetEnum>(
        EN_DOCUMENTS.length > 0 ? initialSet : DocSetEnum.cs
    );

    const isEn = set === DocSetEnum.en;
    const items = isEn ? EN_DOCUMENTS : CS_DOCUMENTS;

    const changeSet = useCallback(
        (next: DocSetEnum) => {
            setSet(next);

            const query = { ...router.query };
            if (next === DocSetEnum.cs) {
                delete query[DOC_SET_QUERY_KEY];
            } else {
                query[DOC_SET_QUERY_KEY] = next;
            }

            /*
                shallow: neběží znovu getServerSideProps; scroll: false: stránka
                nepodskočí. replace (ne push): přepnutí sady je volba zobrazení,
                ne navigace — nemá zakládat záznam v historii.
             */
            void router.replace(
                { pathname: router.pathname, query },
                undefined,
                {
                    shallow: true,
                    scroll: false,
                }
            );
        },
        [router]
    );

    const renderItem = (doc: DocumentDef): ReactElement =>
        isEn ? (
            <DocumentItem
                key={doc.key}
                href={enHref(doc)}
                title={doc.en.title}
                lang={DocSetEnum.en}
            />
        ) : (
            <DocumentItem
                key={doc.key}
                href={csHref(doc)}
                title={getTranslation(t, TNamespaceEnum.document, doc.key)}
                lang={DocSetEnum.cs}
                newWindow={doc.cs.newWindow !== false}
                alt={
                    IS_PARTIAL && hasFreshEn(doc)
                        ? {
                              href: enHref(doc),
                              title: doc.en.title,
                              lang: DocSetEnum.en,
                              label: getTranslation(
                                  t,
                                  TNamespaceEnum.document,
                                  'altLinkLabel'
                              ),
                              note: getTranslation(
                                  t,
                                  TNamespaceEnum.document,
                                  'altLinkNote'
                              ),
                          }
                        : undefined
                }
            />
        );

    return (
        <>
            {EN_DOCUMENTS.length > 0 && (
                <DocumentSetSwitch
                    className={styles.switch}
                    groupLabel={getTranslation(
                        t,
                        TNamespaceEnum.document,
                        'setSwitchLabel'
                    )}
                    value={set}
                    onChange={changeSet}
                    options={[
                        {
                            value: DocSetEnum.cs,
                            label: DOC_SET_LABELS[DocSetEnum.cs],
                            count: CS_DOCUMENTS.length,
                            lang: DocSetEnum.cs,
                        },
                        {
                            value: DocSetEnum.en,
                            label: DOC_SET_LABELS[DocSetEnum.en],
                            count: EN_DOCUMENTS.length,
                            lang: DocSetEnum.en,
                        },
                    ]}
                />
            )}

            {/*
                Live region MUSÍ být v DOM od začátku — region vytvořený současně
                se svým textem čtečky typicky ignorují. Mění se jen jeho text.
             */}
            <p
                role="status"
                aria-live="polite"
                className={styles.visuallyHidden}
            >
                {t(
                    getTranslationKey(
                        TNamespaceEnum.document,
                        isEn ? 'setStatusEn' : 'setStatusCs'
                    ),
                    { count: items.length }
                )}
            </p>

            {DOC_SECTIONS.map((section: DocSection) => {
                const sectionItems = items.filter(
                    (doc) => doc.section === section
                );

                // Prázdná sekce nevytvoří ani <h2>, ani prázdný <ul>.
                if (sectionItems.length === 0) {
                    return null;
                }

                return (
                    <section
                        key={section}
                        lang={isEn ? DocSetEnum.en : undefined}
                    >
                        <h2>
                            {isEn
                                ? EN_SECTION_LABELS[section]
                                : getTranslation(
                                      t,
                                      TNamespaceEnum.document,
                                      section
                                  )}
                        </h2>
                        <ul className={styles.list}>
                            {sectionItems.map(renderItem)}
                        </ul>
                    </section>
                );
            })}
        </>
    );
};

export default DocumentsBlock;
